<script setup lang="ts">
import merge from 'lodash-es/merge';
import type { ListItemModel as LabelListItemModel } from '#layers/prawopl/app/pages/cms/modules/wk-labels/_runtime/services/LabelsService';
import LabelsService from '#layers/prawopl/app/pages/cms/modules/wk-labels/_runtime/services/LabelsService';
import type { ListItemModel as SectionListItemModel } from '#layers/prawopl/app/pages/cms/modules/wk-sections/_runtime/services/SectionsService';
import SectionsService from '#layers/prawopl/app/pages/cms/modules/wk-sections/_runtime/services/SectionsService';
import type { ListItemModel as TopicListItemModel } from '#layers/prawopl/app/pages/cms/modules/wk-topics/_runtime/services/TopicsService';
import TopicsService from '#layers/prawopl/app/pages/cms/modules/wk-topics/_runtime/services/TopicsService';

defineOptions({
    name: 'WkArticlesSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue',): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const defaults = ref<any>({
    mode: 'single',
    isPhotoView: false,
    headlineCoverId: null,
    documentType: null,
    sectionId: null,
    labelId: null,
    topicId: null
});

const form = computed<any>(() =>
{
    return props.modelValue;
});

const documentTypes = computed<Option[]>(() =>
{
    return [
        { value: null, text: $t('[[[Brak sekcji dokumentów]]]') },
        { value: 'Sections', text: $t('[[[Sekcje]]]') },
        { value: 'Labels', text: $t('[[[Etykiety rodzajowe]]]') },
        { value: 'Topics', text: $t('[[[Etykiety tematyczne]]]') }
    ];
});

async function fetchLabel(id: number): Promise<LabelListItemModel>
{
    const response = await LabelsService.fetchAutocomplete(id);

    return response || null;
}

async function searchLabel(query: string, limit: number): Promise<LabelListItemModel[]>
{
    return await LabelsService.getAutocomplete(query, limit);
}

async function fetchSection(id: number): Promise<SectionListItemModel>
{
    const response = await SectionsService.fetchAutocomplete(id);

    return response || null;
}

async function searchSection(query: string, limit: number): Promise<SectionListItemModel[]>
{
    return await SectionsService.getAutocomplete(query, limit);
}

async function fetchTopic(id: number): Promise<TopicListItemModel>
{
    const response = await TopicsService.fetchAutocompleteModel(id);

    return response || null;
}

async function searchTopic(query: string): Promise<TopicListItemModel[]>
{
    return await TopicsService.getAutocompleteModel(query, 100);
}

function documentTypeChanged(): void
{
    form.value.settings.sectionId = null;
    form.value.settings.labelId = null;
    form.value.settings.topicId = null;
}

watch(() => form.value.settings, () =>
{
    triggerModel();
}, { deep: true });

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

watch(() => form.value.settings.sectionId, () =>
{
    onSectionIdChanged();
}, { deep: true });

function onSectionIdChanged(): void
{
    form.value.$errors.clear('settings.sectionId');
}

async function validate(): Promise<void>
{
    const isSections = form.value.settings.documentType === 'Sections';

    form.value.validate((schema: Schema) => ({
        settings: schema.object({
            sectionId: isSections
                ? schema.number().nullable().required($t('[[[Musisz podać nazwę sekcji]]]'))
                : schema.number().nullable()
        })
    }));
}

onBeforeMount((): void =>
{
    form.value.settings = merge(defaults.value, form.value.settings);
});

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Tryb wyświetlania]]]')" :invalid-feedback="form.$errors.first('settings.mode')" :state="form.$errors.state('settings.mode')">
        <IdeoFormRadio v-model="form.settings.mode" name="articles" value="single">
            {{ $t('[[[Pojedynczy artykuł (strona opisowa)]]]') }}
        </IdeoFormRadio>
        <IdeoFormRadio v-model="form.settings.mode" name="articles" value="list">
            {{ $t('[[[Lista artykułów]]]') }}
        </IdeoFormRadio>
    </IdeoFormGroup>

    <div v-if="form.settings.mode === 'list'">
        <IdeoFormGroup :label="$t('[[[Wybierz sekcje dokumentów]]]')">
            <IdeoFormSelect v-model="form.settings.documentType" name="documentType" :options="documentTypes" @change="documentTypeChanged()" />
        </IdeoFormGroup>

        <IdeoFormGroup v-if="form.settings.documentType === 'Sections' || form.settings.documentType === 'Labels'" :label="$t('[[[Wybierz sekcje]]]')" :invalid-feedback="form.$errors.first('settings.sectionId')" :state="form.$errors.state('settings.sectionId')" :required="form.settings.documentType === 'Sections'">
            <IdeoAutocomplete
                v-model="form.settings.sectionId"
                name="sectionId"
                track-by="id"
                :placeholder="form.settings.documentType === 'Sections' ? $t('[[[Wyszukaj sekcję...]]]') : $t('[[[Wszystkie sekcje]]]')"
                :default-label="form.settings.documentType === 'Sections' ? $t('[[[Brak sekcji]]]') : $t('[[[Wszystkie sekcje]]]')"
                :fetch="(id: number) => fetchSection(id)"
                :search="(query: string, limit: number) => searchSection(query, limit)"
            >
                <template #selected="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
                <template #option="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
            </IdeoAutocomplete>
        </IdeoFormGroup>

        <IdeoFormGroup v-if="form.settings.documentType === 'Labels'" :label="$t('[[[Wybierz etykietę rodzajową]]]')">
            <IdeoAutocomplete
                v-model="form.settings.labelId"
                name="labelId"
                track-by="id"
                :placeholder="$t('[[[Wyszukaj etykietę rodzajową...]]]')"
                :default-label="$t('[[[Brak etykiety rodzajowej]]]')"
                :fetch="(id: number) => fetchLabel(id)"
                :search="(query: string, limit: number) => searchLabel(query, 100)"
            >
                <template #selected="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
                <template #option="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
            </IdeoAutocomplete>
        </IdeoFormGroup>

        <IdeoFormGroup v-if="form.settings.documentType === 'Topics'" :label="$t('[[[Wybierz etykietę tematyczną]]]')">
            <IdeoAutocomplete
                v-model="form.settings.topicId"
                name="topicId"
                track-by="id"
                :placeholder="$t('[[[Wyszukaj etykietę tematyczną...]]]')"
                :default-label="$t('[[[Brak etykiety tematycznej]]]')"
                :fetch="(id: number) => fetchTopic(id)"
                :search="(query: string) => searchTopic(query)"
                :limit="300"
            >
                <template #selected="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
                <template #option="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
            </IdeoAutocomplete>
        </IdeoFormGroup>
    </div>
</template>
